import * as XLSX from 'xlsx'
import type { Expense } from './db'
import { CATEGORIES } from '../pages/record/constants'

/** 格式化日期 YYYY-MM-DD → Date 物件（Excel 序列值） */
function toExcelDate(iso: string): Date {
  return new Date(iso + 'T00:00:00')
}

/** 產生 .xlsx 並下載或分享 */
export async function exportToExcel(expenses: Expense[], statsStartMonth: string | null): Promise<void> {
  const wb = XLSX.utils.book_new()

  // ─── 工作表：花費明細 ─────────────────────────────────
  // 第 1-3 列：空白（對齊原檔格式，表頭在第 4 列）
  const aoa: unknown[][] = [[], [], []]

  // 表頭（第 4 列）
  aoa.push([
    '日期', '類別', '項目說明', '金額 (NT$)', '當時里程 (km)', '備註', '年', '月份',
    '油品', '公升數', '公升數是否估算', '加滿',
  ])

  // 依日期升冪排序
  const sorted = [...expenses]
    .filter(e => e.deleted_at === null)
    .sort((a, b) => a.date.localeCompare(b.date))

  sorted.forEach((e, i) => {
    const rowNum = i + 5  // Excel row number（1-based），資料從第 5 列開始
    const dateVal = toExcelDate(e.date)
    aoa.push([
      dateVal,
      e.category,
      e.description ?? '',
      e.amount,
      e.odometer_km ?? '',
      e.note ?? '',
      { f: `YEAR(A${rowNum})` },   // G：年（公式）
      { f: `MONTH(A${rowNum})` },  // H：月份（公式）
      e.fuel_grade ?? '',
      e.liters ?? '',
      // 公升數是否估算：liters 有值且 reminder_rule_id 是 null → 估算
      e.liters != null ? (e.reminder_rule_id === null ? '是' : '否') : '',
      e.is_full_tank != null ? (e.is_full_tank ? '是' : '否') : '',
    ])
  })

  const detailSheet = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true })

  // 設定日期欄格式
  const dateFormat = 'yyyy/mm/dd'
  for (let r = 4; r < aoa.length; r++) {
    const cellAddr = XLSX.utils.encode_cell({ r, c: 0 })
    if (detailSheet[cellAddr]) {
      detailSheet[cellAddr].t = 'd'
      if (!detailSheet[cellAddr].z) detailSheet[cellAddr].z = dateFormat
    }
  }

  XLSX.utils.book_append_sheet(wb, detailSheet, '花費明細')

  // ─── 工作表：類別 ─────────────────────────────────────
  const catAoa: string[][] = [['類別'], ...CATEGORIES.map(c => [c])]
  const catSheet = XLSX.utils.aoa_to_sheet(catAoa)
  XLSX.utils.book_append_sheet(wb, catSheet, '類別')

  // ─── 工作表：總覽（只寫統計起始月） ──────────────────
  const overviewAoa: unknown[][] = []
  for (let i = 0; i < 13; i++) overviewAoa.push([])  // 空白 1-13 列
  // B14（row index 13, col index 1）
  const startDate = statsStartMonth ? new Date(statsStartMonth + 'T00:00:00') : ''
  overviewAoa.push(['', startDate])
  const overviewSheet = XLSX.utils.aoa_to_sheet(overviewAoa, { cellDates: true })
  if (statsStartMonth) {
    const b14 = overviewSheet['B14']
    if (b14) b14.z = 'yyyy/mm/dd'
  }
  XLSX.utils.book_append_sheet(wb, overviewSheet, '總覽')

  // ─── 輸出檔案 ─────────────────────────────────────────
  const today = new Date()
  const ymd =
    today.getFullYear().toString() +
    String(today.getMonth() + 1).padStart(2, '0') +
    String(today.getDate()).padStart(2, '0')
  const filename = `重機花費記帳表_${ymd}.xlsx`

  const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([wbOut], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  // 優先用 Web Share API（iPhone PWA 可存到「檔案」）
  if (
    typeof navigator.share === 'function' &&
    navigator.canShare?.({ files: [new File([blob], filename)] })
  ) {
    await navigator.share({
      files: [new File([blob], filename, { type: blob.type })],
      title: filename,
    })
  } else {
    // 退回直接下載
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}
